/**
 * TeacherCostLedger — "What one teacher costs each month"
 * A plain ledger: 24 months, two columns (accrued vs payroll only).
 * Months 2–23 are identical and collapse to one row. The last row is the point.
 *
 * File name matches the `[chart 'education-gratuity-accrual']` shortcode in the
 * international-school gratuity post. Ported from the "Teacher Cost Ledger"
 * design comp with the house brand-fit pass: brand teal ramp, PS Global logo in
 * the head row, standard panel padding/shadow, compact (container < 640px)
 * edge-to-edge treatment, prose-isolation reset for blog embeds, row reveal
 * gated on scroll-into-view, self-refreshing injected styles. No props.
 */
'use client';

import React, { useEffect } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

const SALARY = 50000, GRATUITY = 10500, ACCRUED = SALARY + GRATUITY, FINAL = 302000, MONTHS = 24;
const fmt = (n: number): string => n.toLocaleString('en-US');

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'tcl-styles';
const CSS = `
.tcl-root { --tcl-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3; --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --teal-wash: #ecfbf9; --gold: #B08A2E; --gold-deep: #7A5F1D; --gold-wash: #F3EAD3;
  position: relative; box-sizing: border-box; width: 100%; max-width: 760px; background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35); padding: 40px 44px 28px; color: var(--ink); font-family: var(--tcl-font); -webkit-font-smoothing: antialiased; }
.tcl-root *, .tcl-root *::before, .tcl-root *::after { box-sizing: border-box; margin: 0; padding: 0; font-family: var(--tcl-font); }
.tcl-root h1, .tcl-root h2, .tcl-root h3, .tcl-root p, .tcl-root img { margin: 0; }
.tcl-root p { letter-spacing: normal; }
.tcl-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.tcl-root .head-text { flex: 0 1 75%; max-width: 75%; } /* house rule: title + description stay within 75% on desktop so the logo always has room */
.tcl-root .tcl-logo { height: 40px; width: auto; flex: none; }
.tcl-root.compact .tcl-logo { display: none; }
.tcl-root.compact .head-text { flex-basis: 100%; max-width: 100%; }
.tcl-root h1 { font-size: 30px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.25; text-wrap: pretty; }
.tcl-root h1 .gold { color: var(--gold); }
.tcl-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; line-height: 1.5; }
.tcl-root table { width: 100%; border-collapse: collapse; margin-top: 40px; font-variant-numeric: tabular-nums; }
.tcl-root th { text-align: right; font-size: 11.5px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); padding: 0 0 12px; border-bottom: 1px solid var(--line); vertical-align: bottom; }
.tcl-root th:first-child { text-align: left; }
.tcl-root th.acc { color: var(--teal-deep); }
.tcl-root td { padding: 16px 0; font-size: 18px; font-weight: 500; text-align: right; border-bottom: 1px solid var(--line-2); color: var(--ink-2); }
.tcl-root td:first-child { text-align: left; font-size: 18px; font-weight: 600; color: var(--ink); }
.tcl-root td:first-child small { display: block; font-size: 12px; font-weight: 400; color: var(--muted); margin-top: 2px; }
.tcl-root td .cur { font-size: 11px; font-weight: 500; color: var(--muted); margin-left: 4px; letter-spacing: 0.04em; }
.tcl-root td.acc { color: var(--teal-deep); font-weight: 600; }
.tcl-root tr.last td { border-bottom: none; padding-top: 18px; vertical-align: baseline; }
.tcl-root tr.last td.pay { color: var(--gold); font-weight: 600; font-size: 26px; letter-spacing: -0.01em; }
.tcl-root tr.last td.pay small { display: block; font-size: 12px; font-weight: 500; letter-spacing: 0; color: var(--gold); margin-top: 2px; }
.tcl-root tr.total td { border-top: 1px solid var(--ink-2); border-bottom: none; padding-top: 14px; font-size: 18px; font-weight: 600; color: var(--ink); }
.tcl-root tr.total td:first-child { font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-2); }
.tcl-root tr.total td small { display: block; font-size: 12px; font-weight: 400; letter-spacing: 0; text-transform: none; color: var(--muted); margin-top: 2px; }
.tcl-root .row-pre { opacity: 0; }
.tcl-root .row-in { animation: tcl-in .5s cubic-bezier(0.19,1,0.22,1) both; }
@keyframes tcl-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.tcl-root .foot { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--line); }
.tcl-root .foot .cap { font-size: 12.5px; line-height: 1.55; color: var(--body); font-style: italic; text-wrap: pretty; }
.tcl-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.tcl-root.compact h1 { font-size: 22px; }
.tcl-root.compact .sub { font-size: 13px; }
.tcl-root.compact table { margin-top: 28px; }
.tcl-root.compact td { font-size: 15px; padding: 13px 0; }
.tcl-root.compact td:first-child { font-size: 15px; white-space: nowrap; padding-right: 10px; }
.tcl-root.compact td:first-child small { font-size: 11px; }
.tcl-root.compact td .cur { font-size: 10px; margin-left: 3px; }
.tcl-root.compact td.acc, .tcl-root.compact th.acc { padding-right: 22px; }
.tcl-root.compact tr.last td.pay small { font-size: 11px; white-space: nowrap; line-height: 1.4; }
.tcl-root.compact tr.total td:first-child { font-size: 11.5px; letter-spacing: 0.06em; }
.tcl-root.compact tr.total td small { font-size: 11px; }
.tcl-root.compact tr.last td.pay { font-size: 21px; }
.tcl-root.compact tr.total td { font-size: 15px; }
.tcl-root.compact th { font-size: 10.5px; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useCompact(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [compact, setCompact] = React.useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const measure = (): void => { const w = el.offsetWidth; if (w > 0) setCompact(w < 640); };
    measure(); const raf = window.requestAnimationFrame(measure); window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.cancelAnimationFrame(raf); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [ref]);
  return compact;
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
const TeacherCostLedger: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const compact = useCompact(rootRef);
  const inView = useInView(rootRef);
  const row = (i: number, extra = ''): { className: string; style: React.CSSProperties } => ({
    className: `${inView ? 'row-in' : 'row-pre'}${extra ? ' ' + extra : ''}`,
    style: { animationDelay: `${120 + i * 90}ms` },
  });
  const total = ACCRUED * MONTHS;

  return (
    <div className={`tcl-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div className="head-text">
          <h1>What <span className="gold">one</span> teacher costs each month</h1>
          <p className="sub">Same teacher, same two-year contract &mdash; booked two ways.</p>
        </div>
        <img className="tcl-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      </div>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th className="acc">With accrual</th>
            <th>Payroll only</th>
          </tr>
        </thead>
        <tbody>
          <tr {...row(0)}><td>1</td><td className="acc">{fmt(ACCRUED)}<span className="cur">HKD</span></td><td className="pay">{fmt(SALARY)}<span className="cur">HKD</span></td></tr>
          <tr {...row(1)}><td>2 &ndash; 23<small>22 identical months</small></td><td className="acc">{fmt(ACCRUED)}<span className="cur">HKD</span></td><td className="pay">{fmt(SALARY)}<span className="cur">HKD</span></td></tr>
          <tr {...row(2, 'last')}><td>24<small>Contract ends</small></td><td className="acc">{fmt(ACCRUED)}<span className="cur">HKD</span></td><td className="pay">{fmt(FINAL)}<span className="cur">HKD</span><small>{compact ? <React.Fragment>salary +<br />252,000 gratuity</React.Fragment> : 'salary + HK$252,000 gratuity'}</small></td></tr>
          <tr {...row(3, 'total')}><td>Two years<small>24 months</small></td><td>{fmt(total)}<span className="cur">HKD</span></td><td>{fmt(total)}<span className="cur">HKD</span></td></tr>
        </tbody>
      </table>
      <div className="foot">
        <p className="cap">HK$50,000 salary, 21% gratuity, two-year contract. Accrued, the cost is HK$60,500 every month. Booked from payroll alone, it&rsquo;s HK$50,000 for 23 months and HK$302,000 in the month the contract ends.</p>
      </div>
    </div>
  );
};

export default TeacherCostLedger;
